import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { Search, X } from "lucide-react";
import { PurchaseDocListItem } from "types/InvoiceInbox";
import { fetchPurchaseDocs, mergePurchaseDocInto } from "services/invoice-inbox.service";
import { formatAmount } from "utils/invoiceInboxStatus";
import { refusalMessage } from "./invoice-doc-files";

/**
 * "Attach to another document": fold this register row into another one as
 * additional files.
 *
 * Why it exists: the email intake makes one row per attachment, so an invoice
 * and its work act (or an invoice split over two PDFs) sent in one email
 * arrive as two rows — and the second one is not a document of its own. The
 * server moves this row's files into the target and soft-deletes this row;
 * it allows that only before anybody has been asked to approve it
 * (new / needs_review / rejected), and refuses otherwise.
 */

type Props = {
  companyId: string;
  doc: Pick<PurchaseDocListItem, "id" | "docNumber">;
  onClose: () => void;
  /** After a successful merge — this document no longer exists in the register. */
  onMerged: (target: PurchaseDocListItem) => void;
};

const SEARCH_DEBOUNCE_MS = 300;
const MAX_CANDIDATES = 25;

export default function InvoiceDocMergeDialog({ companyId, doc, onClose, onMerged }: Props) {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => Boolean((i18n as any).exists && (i18n as any).exists(key));
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<PurchaseDocListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<PurchaseDocListItem | null>(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  // An empty query lists the newest documents — the other half of an email
  // usually arrived right next to this one.
  useEffect(() => {
    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const data = await fetchPurchaseDocs(companyId, {
          tab: "all",
          page: 1,
          filtersValue: { textSearch: query.trim() },
        });
        if (requestId !== requestRef.current) return;
        setCandidates(
          (data.docs || []).filter((d) => d.id !== doc.id && !d.deletedAt).slice(0, MAX_CANDIDATES)
        );
        setError(null);
      } catch (err) {
        if (requestId !== requestRef.current) return;
        setCandidates([]);
        setError(refusalMessage(t, exists, err));
      } finally {
        if (requestId === requestRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `t`/`exists` change identity per render; the search depends on the query only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, doc.id, query]);

  const confirm = async () => {
    if (!picked) return;
    setMerging(true);
    setError(null);
    try {
      // The server answers only the target's id (`result.target`), not a full
      // list row — the picked row already IS the one the caller needs.
      await mergePurchaseDocInto(companyId, doc.id, picked.id);
      toast.success(
        t("invoice_inbox_merge_done", {
          defaultValue: "Attached to №{{number}}",
          number: picked.docNumber ?? "—",
        })
      );
      onMerged(picked);
    } catch (err) {
      setError(refusalMessage(t, exists, err));
    } finally {
      setMerging(false);
    }
  };

  const describe = (d: PurchaseDocListItem) =>
    [
      `№${d.docNumber ?? "—"}`,
      d.supplierNameRaw || "—",
      d.invoiceNumber || "—",
      d.issueDate || "—",
      formatAmount(d.grossAmount, d.currency),
    ].join(" · ");

  return (
    <Dialog open onClose={merging ? () => {} : onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-start justify-center p-2 sm:p-6">
        <DialogPanel className="mt-8 flex max-h-[85dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10">
          <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {t("invoice_inbox_merge", { defaultValue: "Attach to another document" })}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              disabled={merging}
              aria-label={t("close", { defaultValue: "Close" })}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-md:p-3"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-2 border-b border-gray-200 px-4 py-3">
            <p className="text-sm text-gray-600">
              {t("invoice_inbox_merge_hint", {
                defaultValue:
                  "An invoice and its work act sent in one email arrive as two rows. Find the document this one belongs to.",
              })}
            </p>
            <label className="relative block">
              <Search aria-hidden className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPicked(null);
                }}
                placeholder={t("invoice_inbox_search_placeholder", {
                  defaultValue: "Supplier, invoice number, file name…",
                })}
                aria-label={t("invoice_inbox_merge_search", { defaultValue: "Search documents" })}
                className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-base md:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {searching && !candidates.length ? (
              <div className="p-4 text-sm text-gray-500">{t("loading", { defaultValue: "Loading…" })}</div>
            ) : !candidates.length ? (
              <div className="p-4 text-sm text-gray-500">
                {t("invoice_inbox_merge_no_results", { defaultValue: "Nothing found" })}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {candidates.map((candidate) => (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setPicked(candidate);
                        setError(null);
                      }}
                      aria-pressed={picked?.id === candidate.id}
                      className={clsx(
                        "block w-full px-4 py-2 text-left text-sm max-md:py-3",
                        picked?.id === candidate.id ? "bg-blue-50 text-blue-900" : "text-gray-800 hover:bg-gray-50"
                      )}
                    >
                      {describe(candidate)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(picked || error) && (
            <div className="space-y-2 border-t border-gray-200 px-4 py-3">
              {error && <p className="text-sm text-red-700">{error}</p>}
              {picked && (
                <>
                  <p className="text-sm text-gray-800">
                    {t("invoice_inbox_merge_confirm_text", {
                      defaultValue:
                        "This document will be attached to №{{number}} as additional files and disappear from the register.",
                      number: picked.docNumber ?? "—",
                    })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={merging}
                      onClick={confirm}
                      className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50 max-md:min-h-11"
                    >
                      {t("confirm", { defaultValue: "Confirm" })}
                    </button>
                    <button
                      type="button"
                      disabled={merging}
                      onClick={() => setPicked(null)}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 max-md:min-h-11"
                    >
                      {t("cancel", { defaultValue: "Cancel" })}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
